import React from "react";
import { AmountType, GiftOffer, GiftOfferType, PurchaseRule } from "types";
import { useResource } from "../../hooks/useResource";
import { Currency } from "../Common/Currency";
import { getBlackoutSchedules, getWindowSchedule } from "../Coupon/CouponRestrictionForm";
import { getTerms } from "../Product/EditGiftOffer";
import { getProductRestrictions } from "../Product/EditGiftOfferRestrictions";
import { DateRange } from "@/components/Common/ScheduleSummary";
import {
    ProductRestrictionsList,
    PurchaseRuleTile,
} from "@/components/PurchaseCreate/CreatePurchaseProductRestrictions";

interface CreatePurchaseGiftRestrictionsProps {
    product: GiftOffer;
    purchaseRule?: PurchaseRule;
}

export const CreatePurchaseGiftRestrictions = ({ product, purchaseRule }: CreatePurchaseGiftRestrictionsProps) => {
    const { data: redemptionPurchaseRule } = useResource<PurchaseRule>([
        "purchaseRules",
        product?.redemptionPurchaseRule?.id,
    ]);

    const giftOfferTerms = getTerms(product, purchaseRule);

    const purchaseWindowSchedule = purchaseRule ? getWindowSchedule(purchaseRule, "currentTime") : null;
    const arrivalWindowSchedule = redemptionPurchaseRule
        ? getWindowSchedule(redemptionPurchaseRule, "arrivalDateTime")
        : null;
    const productRestrictions = redemptionPurchaseRule
        ? getProductRestrictions(redemptionPurchaseRule, "product.id")
        : null;

    const purchaseBlackoutSchedules = purchaseRule ? getBlackoutSchedules(purchaseRule, "currentTime") : null;
    const arrivalBlackoutSchedules = redemptionPurchaseRule
        ? getBlackoutSchedules(redemptionPurchaseRule, "arrivalDateTime")
        : null;

    return (
        <div className="space-y-1">
            <h3>Gift Summary</h3>
            <div className="flex w-full space-x-1">
                {giftOfferTerms.type === GiftOfferType.FIXED ? (
                    <>
                        <PurchaseRuleTile title="Purchase Price">
                            <Currency>{giftOfferTerms.purchasePrice}</Currency>
                        </PurchaseRuleTile>
                        <PurchaseRuleTile title="Gift Value">
                            <Currency>{giftOfferTerms.price}</Currency>
                        </PurchaseRuleTile>
                    </>
                ) : (
                    <>
                        <PurchaseRuleTile title="Discount">
                            {giftOfferTerms.discountAmountType === AmountType.ABSOLUTE ? (
                                <Currency>{giftOfferTerms.discountAmount}</Currency>
                            ) : (
                                `${giftOfferTerms.discountAmount}%`
                            )}
                        </PurchaseRuleTile>
                        <PurchaseRuleTile title="Minimum Price">
                            <Currency>{giftOfferTerms.minimumPrice}</Currency>
                        </PurchaseRuleTile>
                    </>
                )}
            </div>

            <PurchaseRuleTile title="Product Restrictions">
                <ProductRestrictionsList productRestrictions={productRestrictions} />
            </PurchaseRuleTile>

            <div className="flex w-full space-x-1">
                <PurchaseRuleTile title="Purchase Date Restrictions">
                    {purchaseWindowSchedule ? (
                        <DateRange
                            key={purchaseWindowSchedule.id}
                            start={purchaseWindowSchedule.start}
                            end={purchaseWindowSchedule.end}
                        />
                    ) : (
                        "None"
                    )}
                </PurchaseRuleTile>

                <PurchaseRuleTile title="Arrival Restrictions">
                    {arrivalWindowSchedule ? (
                        <DateRange
                            key={arrivalWindowSchedule.id}
                            start={arrivalWindowSchedule.start}
                            end={arrivalWindowSchedule.end}
                        />
                    ) : (
                        "None"
                    )}
                </PurchaseRuleTile>
            </div>

            <div className="flex w-full space-x-1">
                <PurchaseRuleTile title="Purchase Date Blackout Restrictions">
                    {purchaseBlackoutSchedules
                        ? purchaseBlackoutSchedules.map((schedule) => (
                              <DateRange key={schedule.id} start={schedule.start} end={schedule.end} />
                          ))
                        : "None"}
                </PurchaseRuleTile>

                <PurchaseRuleTile title="Arrival Blackout Restrictions">
                    {arrivalBlackoutSchedules
                        ? arrivalBlackoutSchedules.map((schedule) => (
                              <DateRange key={schedule.id} start={schedule.start} end={schedule.end} />
                          ))
                        : "None"}
                </PurchaseRuleTile>
            </div>
        </div>
    );
};
